// src/App.js

import React, { useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import authService from './apiServices/authService';
import Login from './auth/LogIn';
import RegisterPage from './auth/RegisterPage';
import ForgotPassword from './auth/ForgotPassword';
import StudentRegistration from './auth/StudentRegistration';
import StudentProfile from './pages/student/StudentProfile';
import StudentDashboard from './pages/student/StudentDashboard';
import SupervisorRegistration from './auth/SupervisorRegistration';
import SupervisorProfile from './pages/supervisor/SupervisorProfile';
import SupervisorDashboard from './pages/supervisor/SupervisorDashboard';
import UniversityAdminRegistration from './auth/UniversityAdminRegistration';
import UniversityAdminProfile from './pages/institution-admin/UniversityAdminProfile';
import ProtectedRoute from './components/ProtectedRoute';
import NotFoundPage from './components/NotFoundPage';
import ResearchTopic from './pages/student/ResearchTopic';
import TopicSimilarityReport from './pages/student/TopicSimilarityReport';
import SuggestedSupervisors from './pages/student/SuggestedSupervisors';
import WorkflowProgress from './pages/student/WorkflowProgress';
import FeedbackAndFiles from './pages/student/FeedbackAndFiles';
import PublicationStatus from './pages/student/PublicationStatus';
import ResearchWorkUpload from './pages/student/ResearchWorkUpload';
import AssignedStudents from './pages/supervisor/AssignedStudents';
import PendingReview from './pages/supervisor/PendingReview';
import SupervisionCapacity from './pages/supervisor/SupervisionCapacity';
import ResearchPerformanceMetrics from './pages/supervisor/ResearchPerformanceMetrics';
import StudentFeedbackAnalytics from './pages/supervisor/StudentFeedbackAnalytics';
import UserManagement from './pages/institution-admin/UserManagement';
import SupervisorApproval from './pages/institution-admin/SupervisorApproval';
import TopicOversight from './pages/institution-admin/TopicOversight';
import InstitutionSetting from './pages/institution-admin/InstitutionSetting';
import CrossInstitutionAnalytics from './pages/platform-admin/CrossInstitutionAnalytics';
import Universities from './pages/platform-admin/Universities';
import UserLogin from './auth/UserLogin';
import TopicSimilarityAnalysis from './pages/platform-admin/TopicSimilarityAnalysis';
import SupervisorRanking from './pages/platform-admin/SupervisorRanking';
import PlatformDashboard from './pages/platform-admin/PlatformDashboard';
import PlatformProfile from './pages/platform-admin/PlatformProfile';
import Supervisor from './pages/platform-admin/Supervisor';
import Student from './pages/platform-admin/Student';
import UniversityAdmin from './pages/platform-admin/UniversityAdmin';
import StudentApproval from './pages/institution-admin/StudentApproval';
import AssignSupervisor from './pages/institution-admin/AssignSupervisor';
import AssignedStudent from './pages/platform-admin/AssignedStudent';
import SupervisorChat from './pages/supervisor/SupervisorChat';
import AssignedSupervisor from './pages/student/AssignedSupervisor';
import Assigned from './pages/platform-admin/Assigned';
import SupervisorTopicView from './pages/supervisor/SupervisorTopicView';
import PlatformTopicView from './pages/platform-admin/PlatformTopicView';
import EmailVerification from './auth/EmailVerification';
import ResetPassword from './auth/ResetPassword';
import SupervisorForgotPassword from './auth/SupervisorForgotPassword';
import SupervisorEmailVerification from './auth/SupervisorEmailVerification';
import SupervisorResetPassword from './auth/SupervisorResetPassword';
import InstitutionAdminForgotPassword from './auth/InstitutionAdminForgotPassword';
import InstitutionAdminEmailVerification from './auth/InstitutionAdminEmailVerification';
import InstitutionAdminResetPassword from './auth/InstitutionAdminResetPassword';
import InstitutionTopicView from './pages/institution-admin/InstitutionTopicView';
import InstitutionResearchWorkView from './pages/institution-admin/InstitutionResearchWorkView';
import SupervisorResearchWorkView from './pages/supervisor/SupervisorResearchWorkView';
import PlatformAdminWorkView from './pages/platform-admin/PlatformAdminWorkView';
import MySpecialty from './pages/supervisor/MySpecialty';
import Ranking from './pages/student/Rankings';
import PlatformAdminNotifications from './pages/platform-admin/PlatformAdminNotifications';
import InstitutionAdminNotifications from './pages/institution-admin/InstitutionAdminNotifications';
import StudentNotifications from './pages/student/StudentNotifications';
import SupervisorNotifications from './pages/supervisor/SupervisorNotifications';
import InstitutionAdminDashboard from './pages/institution-admin/InstitutionAdminDashboard';
import SpecialityView from './pages/institution-admin/SpecialityView';
import Speciality from './pages/platform-admin/Speciality';
import MyInstitution from './pages/supervisor/MyInstitution';
import UniversityHeadRegistration from './auth/UniversityHeadRegistration';
import UniversityHead from './pages/platform-admin/UniversityHead';
import UniversityHeadForgotPassword from './auth/UniversityHeadForgotPassword';
import UniversityHeadEmailVerification from './auth/UniversityHeadEmailVerification';
import UniversityHeadResetPassword from './auth/UniversityHeadResetPassword';
import InstitutionHeadDashboard from './pages/institution-head/InstitutionHeadDashboard';
import InstitutionAdminApprove from './pages/institution-head/InstitutionAdminApprove';
import InstitutionHeadApprove from './pages/platform-admin/InstitutionHeadApprove';
import InstitutionHeadProfile from './pages/institution-head/InstitutionHeadProfile';


// Create theme
const theme = createTheme({
	palette: {
		primary: {
			main: '#1976d2',
			light: '#42a5f5',
			dark: '#1565c0',
			contrastText: '#fff',
		},
		secondary: {
			main: '#2e7d32',
			light: '#4caf50',
			dark: '#1b5e20',
			contrastText: '#fff',
		},
		success: {
			light: '#81c784',
			main: '#4caf50',
			dark: '#388e3c',
			contrastText: '#fff',
		},
		error: {
			light: '#e57373',
			main: '#f44336',
			dark: '#d32f2f',
			contrastText: '#fff',
		},
		warning: {
			light: '#ffb74d',
			main: '#ff9800',
			dark: '#f57c00',
			contrastText: '#fff',
		},
		info: {
			light: '#64b5f6',
			main: '#2196f3',
			dark: '#1976d2',
			contrastText: '#fff',
		},
		background: {
			default: '#f5f7fa',
			paper: '#ffffff',
		},
		common: {
			auth: '#FFFDEB',
			mainBody:'#E4EFE7'
		},
		text: {
			primary: '#2c3e50',
			secondary: '#7f8c8d',
		},
	},
	typography: {
		fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
		h1: {
			fontWeight: 600,
		},
		h2: {
			fontWeight: 600,
		},
		h3: {
			fontWeight: 600,
		},
		h4: {
			fontWeight: 600,
		},
		h5: {
			fontWeight: 600,
		},
		h6: {
			fontWeight: 600,
		},
		button: {
			textTransform: 'none',
			fontWeight: 500,
		},
	},
	shape: {
		borderRadius: 12,
	},
	shadows: [
		'none',
		'0px 2px 4px rgba(0, 0, 0, 0.05)',
		'0px 4px 8px rgba(0, 0, 0, 0.05)',
		'0px 8px 16px rgba(0, 0, 0, 0.05)',
		'0px 12px 24px rgba(0, 0, 0, 0.05)',
		'0px 16px 32px rgba(0, 0, 0, 0.05)',
		'0px 20px 40px rgba(0, 0, 0, 0.05)',
		'0px 24px 48px rgba(0, 0, 0, 0.05)',
		'0px 28px 56px rgba(0, 0, 0, 0.05)',
		'0px 32px 64px rgba(0, 0, 0, 0.05)',
		'0px 36px 72px rgba(0, 0, 0, 0.05)',
		'0px 40px 80px rgba(0, 0, 0, 0.05)',
		'0px 44px 88px rgba(0, 0, 0, 0.05)',
		'0px 48px 96px rgba(0, 0, 0, 0.05)',
		'0px 52px 104px rgba(0, 0, 0, 0.05)',
		'0px 56px 112px rgba(0, 0, 0, 0.05)',
		'0px 60px 120px rgba(0, 0, 0, 0.05)',
		'0px 64px 128px rgba(0, 0, 0, 0.05)',
		'0px 68px 136px rgba(0, 0, 0, 0.05)',
		'0px 72px 144px rgba(0, 0, 0, 0.05)',
		'0px 76px 152px rgba(0, 0, 0, 0.05)',
		'0px 80px 160px rgba(0, 0, 0, 0.05)',
		'0px 84px 168px rgba(0, 0, 0, 0.05)',
		'0px 88px 176px rgba(0, 0, 0, 0.05)',
		'0px 92px 184px rgba(0, 0, 0, 0.05)',
	],
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					padding: '8px 16px',
				},
				contained: {
					boxShadow: 'none',
					'&:hover': {
						boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
					},
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					borderRadius: 16,
					boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					borderRadius: 16,
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					'& .MuiOutlinedInput-root': {
						borderRadius: 8,
					},
				},
			},
		},
	},
});

function App() {
	// Initialize auth service on app start
	// useEffect(() => {
	//     // Set default axios headers if token exists
	//     const token = authService.getAccessToken();
	//     if (token) {
	//         const axios = require('axios');
	//         axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	//     }
	// }, []);

	return (
		<SnackbarProvider
			maxSnack={3}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			autoHideDuration={4000}
		>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<Router>
					<Routes>
						{/* Public Routes */}
						<Route path="/" element={<Navigate to="/login" replace />} />
						<Route path="/login" element={<Login />} />
						<Route path="/register" element={<RegisterPage />} />
						<Route path="/forgot-password" element={<ForgotPassword />} />
						<Route path="/verify-email/:token" element={<EmailVerification />} />
						<Route path="/reset-password" element={<ResetPassword />} />

						<Route path="/supervisor/forgot-password" element={<SupervisorForgotPassword />} />
						<Route path="/supervisor/verify-email/:token" element={<SupervisorEmailVerification />} />
						<Route path="/supervisor/reset-password" element={<SupervisorResetPassword />} />

						<Route path="/institution-admin/forgot-password" element={<InstitutionAdminForgotPassword />} />
						<Route path="/institution-admin/verify-email/:token" element={<InstitutionAdminEmailVerification />} />
						<Route path="/institution-admin/reset-password" element={<InstitutionAdminResetPassword />} />

						<Route path="/institution-head/forgot-password" element={<UniversityHeadForgotPassword />} />
						<Route path="/institution-head/verify-email/:token" element={<UniversityHeadEmailVerification />} />
						<Route path="/institution-head/reset-password" element={<UniversityHeadResetPassword />} />


						{/* Student Routes */}
						<Route path="/student/register" element={<StudentRegistration />} />
						<Route path="/student/profile" element={<ProtectedRoute allowedUserTypes={['student']}><StudentProfile /></ProtectedRoute>}/>
						<Route path="/student/dashboard"element={<ProtectedRoute allowedUserTypes={['student']}><StudentDashboard /></ProtectedRoute>} />
						<Route path="/student/research-topics"element={<ProtectedRoute allowedUserTypes={['student']}><ResearchTopic /></ProtectedRoute>} />
						<Route path="/student/topic-similarity-report"element={<ProtectedRoute allowedUserTypes={['student']}><TopicSimilarityReport /></ProtectedRoute>} />
						<Route path="/student/suggested-supervisor"element={<ProtectedRoute allowedUserTypes={['student']}><SuggestedSupervisors /></ProtectedRoute>} />
						<Route path="/student/work-flow-progress"element={<ProtectedRoute allowedUserTypes={['student']}><WorkflowProgress /></ProtectedRoute>} />
						<Route path="/student/feedback-and-file"element={<ProtectedRoute allowedUserTypes={['student']}><FeedbackAndFiles /></ProtectedRoute>} />
						<Route path="/student/publication-status"element={<ProtectedRoute allowedUserTypes={['student']}><PublicationStatus /></ProtectedRoute>} />
						<Route path="/student/research-work-upload"element={<ProtectedRoute allowedUserTypes={['student']}><ResearchWorkUpload /></ProtectedRoute>} />
						<Route path="/student/assigned-supervisor"element={<AssignedSupervisor/>} />
						<Route path="/student/notifications" element={<StudentNotifications/>} />









						{/* Supervisor Routes */}
						<Route path="/supervisor/register" element={<SupervisorRegistration />} />
						<Route 
							path="/supervisor/profile"
							element={
								<ProtectedRoute allowedUserTypes={['supervisor']}><SupervisorProfile /></ProtectedRoute>} />
						<Route 
							path="/supervisor/dashboard"
							element={
								<ProtectedRoute allowedUserTypes={['supervisor']}>
									<SupervisorDashboard />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/supervisor/assigned-student"
							element={
								<ProtectedRoute allowedUserTypes={['supervisor']}>
									<AssignedStudents />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/supervisor/pending-review"
							element={
								<ProtectedRoute allowedUserTypes={['supervisor']}>
									<PendingReview />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/supervisor/works"
							element={
								<ProtectedRoute allowedUserTypes={['supervisor']}>
									<SupervisorResearchWorkView />
								</ProtectedRoute>
							} 
						/>
						
						<Route 
							path="/supervisor/supervision-capacity"
							element={
								<ProtectedRoute allowedUserTypes={['supervisor']}>
									<SupervisionCapacity />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/supervisor/performance-metrics"
							element={
								<ProtectedRoute allowedUserTypes={['supervisor']}>
									<ResearchPerformanceMetrics />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/supervisor/student-feedback-analytics"
							element={
								<ProtectedRoute allowedUserTypes={['supervisor']}>
									<StudentFeedbackAnalytics />
								</ProtectedRoute>
							} 
						/>
						<Route path="/supervisor/supervisor-chat" element={<SupervisorChat />}/>
						<Route path="/supervisor/topic-view" element={<SupervisorTopicView />}/>
						<Route path="/supervisor/speciality" element={<MySpecialty />}/>
						<Route path="/supervisor/institution" element={<MyInstitution />}/>
						<Route path="/supervisor/notifications" element={<SupervisorNotifications />}/>

						{/* Main routes */}
						<Route
							path="/main/topics-upladed" allowedUserTypes={['student', 'supervisor', 'institution_admin', 'institution_head']}
							element={
								<ProtectedRoute >
									<PlatformTopicView />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/main/projects-upload" allowedUserTypes={['student', 'supervisor', 'institution_admin', 'institution_head']}
							element={
								<ProtectedRoute >
									<PlatformAdminWorkView />
								</ProtectedRoute>
							}
						/>

						{/* institution Admin Routes */}
						<Route path="/institution-admin/register" element={<UniversityAdminRegistration />} />
						<Route 
							path="/institution-admin/profile" 
							element={
								<ProtectedRoute allowedUserTypes={['institution_admin']}>
									<UniversityAdminProfile />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/institution-admin/dashboard"
							element={
								<ProtectedRoute allowedUserTypes={['institution_admin']}>
									<InstitutionAdminDashboard />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/institution-admin/topics"
							element={
								<ProtectedRoute allowedUserTypes={['institution_admin']}>
									<InstitutionTopicView />
								</ProtectedRoute>
							}
						/>
							<Route
							path="/institution-admin/works"
							element={
								<ProtectedRoute allowedUserTypes={['institution_admin']}>
									<InstitutionResearchWorkView />
								</ProtectedRoute>
							}
						/>

						<Route 
							path="/institution-admin/users"
							element={
								<ProtectedRoute allowedUserTypes={['institution_admin']}>
									<UserManagement />
								</ProtectedRoute>
							}
						/>
						<Route 
							path="/institution-admin/supervisor-approval"
							element={
								<ProtectedRoute allowedUserTypes={['institution_admin']}>
									<SupervisorApproval />
								</ProtectedRoute>
							}
						/>
						<Route path="/institution-admin/student-approval" element={<StudentApproval />}/>
						<Route path="/institution-admin/assign-supervisor" element={<AssignSupervisor />}/>
						<Route path="/institution-admin/speciality" element={<SpecialityView />}/>
						<Route path="/institution-admin/notifications" element={<InstitutionAdminNotifications />}/>

						<Route 
							path="/institution-admin/topic-oversight"
							element={
								<ProtectedRoute allowedUserTypes={['institution_admin']}>
									<TopicOversight />
								</ProtectedRoute>
							}
						/>
						<Route 
							path="/institution-admin/institution-setting"
							element={
								<ProtectedRoute allowedUserTypes={['institution_admin']}>
									<InstitutionSetting />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/institution-admin/users"
							element={
								<ProtectedRoute allowedUserTypes={['institution_admin']}>
									<UserManagement />
								</ProtectedRoute>
							}
						/>

						{/* Main Dashboard Route (redirects based on user type) */}
						<Route
							path="/main/dashboard"
							element={
								<ProtectedRoute allowedUserTypes={['student', 'supervisor', 'institution_admin']}>
									<Navigate to={authService.getDashboardUrl()} replace />
								</ProtectedRoute>
							}
						/>


						{/* institution Head Routes */}
						<Route path="/institution-head/register" element={<UniversityHeadRegistration />} />
						<Route 
							path="/institution-head/dashboard" 
							element={
								<ProtectedRoute allowedUserTypes={['institution_head']}>
									<InstitutionHeadDashboard />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/institution-head/profile" 
							element={
								<ProtectedRoute allowedUserTypes={['institution_head']}>
									<InstitutionHeadProfile />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/institution-head/institution-approval" 
							element={
								<ProtectedRoute allowedUserTypes={['institution_head']}>
									<InstitutionAdminApprove />
								</ProtectedRoute>
							} 
						/>
						{/* University Admin Routes */}
						<Route path="/platform-admin/" element={<UserLogin />} />
						<Route path="/platform-admin/login" element={<UserLogin />} />
						<Route path="/platform-admin/dashboard" element={<PlatformDashboard />} />
						<Route path="/platform-admin/profile" element={<PlatformProfile />} />
						<Route path="/platform-admin/universities" element={<Universities />} />
						<Route path="/platform-admin/supervisor-ranking" element={<SupervisorRanking />} />
						<Route path="/platform-admin/cross-institution-analytics" element={<CrossInstitutionAnalytics />}/>
						<Route path="/platform-admin/topic-similarity-analysis" element={<TopicSimilarityAnalysis />}/>
						<Route path="/platform-admin/supervisor" element={<Supervisor />}/>
						<Route path="/platform-admin/institution-head" element={<UniversityHead />}/>
						<Route path="/platform-admin/works" element={<PlatformAdminWorkView />}/>
						<Route path="/platform-admin/student" element={<Student />}/>
						<Route path="/platform-admin/institution-admin" element={<UniversityAdmin />}/>
						<Route path="/platform-admin/speciality" element={<Speciality />}/>
						<Route path="/platform-admin/institution-head-approve" element={<InstitutionHeadApprove />}/>



						<Route path="/platform-admin/assigned-student" element={<AssignedStudent />}/>
						<Route path="/platform-admin/assigned" element={<Assigned />}/>
						<Route path="/platform-admin/topic-view" element={<PlatformTopicView />}/>
						<Route path="/platform-admin/notifications" element={<PlatformAdminNotifications />}/>


						{/* 404 Not Found */}
						<Route path="*" element={<NotFoundPage />} />
					</Routes>
				</Router>
			</ThemeProvider>
		</SnackbarProvider>
	);
}

export default App;